import * as authActions from '@/lib/authActions';
import { type AuthProvider } from '@/utils/auth/persistedAuthProvider';
import { type Region } from '@heidi/schema/ml-scribe';
import { createModel } from '@rematch/core';
import { type RootModel } from '.';

// Re-export for backward compatibility - allows existing imports from authModel to continue working
export {
  AuthProvider,
  getPersistedAuthProvider,
} from '@/utils/auth/persistedAuthProvider';

export type InitialState = {
  token?: string;
  authProvider?: AuthProvider;
  userRegion?: Region;
};

const initialState: InitialState = {
  token: undefined,
  authProvider: undefined,
};

/**
 * Shape of the whole persisted root state passed to `persist/REHYDRATE` —
 * only this model's slice is read.
 */
type PersistedRootPayload = { auth?: InitialState } | undefined;

export const auth = createModel<RootModel>()({
  state: initialState,

  selectors: (slice) => ({
    getToken() {
      return slice((state) => state.token);
    },
  }),
  reducers: {
    _setAuthProvider(state, provider: AuthProvider) {
      if (state.authProvider === provider) return state;
      return {
        ...state,
        authProvider: provider,
      };
    },
    clearAuthProvider(state) {
      if (state.authProvider === undefined) return state;
      return {
        ...state,
        authProvider: undefined,
      };
    },
    setUserRegion(state, region: Region) {
      return {
        ...state,
        userRegion: region,
      };
    },
    _setToken(state, token: string) {
      if (state.token === token) return state;
      return {
        ...state,
        token,
      };
    },

    // Deliberately returns a fresh object even when there is nothing to
    // clear: the two branches this used to have differed only in how far
    // they advanced the revision, and clearing an already-empty slice never
    // did. `useSelector((s) => s.auth)` consumers still see a new identity
    // on every clear, exactly as before.
    clear() {
      return { ...initialState };
    },

    // redux-persist's own REHYDRATE merge writes this slice directly,
    // bypassing every reducer above — see autoMergeLevel1. This entry runs
    // as a normal Rematch reducer (the '/' in the key is a literal action
    // type, not a namespaced name) so a real rehydrated token/provider
    // change lands in the slice rather than being dropped.
    'persist/REHYDRATE': (state: InitialState, payload: PersistedRootPayload) => {
      const restored = payload?.auth;
      if (!restored) return state;
      if (
        restored.token === state.token &&
        restored.authProvider === state.authProvider
      ) {
        return { ...state, userRegion: restored.userRegion ?? state.userRegion };
      }
      return {
        ...state,
        token: restored.token,
        authProvider: restored.authProvider,
        userRegion: restored.userRegion ?? state.userRegion,
      };
    },
  },
  // Every effect below is a one-line delegate to the function of the same name
  // in `@/lib/authActions`, which holds the body that used to live here. The
  // effects stay declared because the Rematch loading plugin derives
  // `state.loading.effects.auth.<name>` from this list.
  //
  // The eleven reads across six production files that used to consult
  // `state.loading.effects.auth.logout` — to suppress a session-expiry logout
  // while an intentional one is in flight — now ask `@/lib/authInFlight`
  // instead, which the logout sequence owns rather than the dispatch wrapper.
  // (The earlier count here said ten across five: `KeycloakSessionGuard.tsx`
  // was missed, because it read through a local structural type rather than
  // `RootState`.) The delegates and the derived flags are retained until the
  // effect-dispatch repoints land; they go when the slice does.
  effects: () => ({
    logoutFromServer() {
      return authActions.logoutFromServer();
    },

    ssoLogout() {
      authActions.ssoLogout();
    },

    logoutFromExtension() {
      authActions.logoutFromExtension();
    },

    logout(payload: authActions.LogoutOptions = {}) {
      return authActions.logout(payload);
    },

    logoutWithoutRedirect() {
      return authActions.logoutWithoutRedirect();
    },

    clearPersistedModels() {
      return authActions.clearPersistedModels();
    },

    setToken(token: string) {
      authActions.setToken(token);
    },

    setAuthProviderAsSessionCookie(authProvider: AuthProvider) {
      authActions.setAuthProviderAsSessionCookie(authProvider);
    },

    setAuthProviderAsPersistentCookie(authProvider: AuthProvider) {
      authActions.setAuthProviderAsPersistentCookie(authProvider);
    },
  }),
});
